/**
 * CloudWatch Logger with Pino integration
 *
 * Features:
 * - Configurable log levels via REAL_ID_LOG_LEVEL env var
 * - Browser console output in development or when localStorage debug flag is set
 * - CloudWatch transmission with hybrid batching (immediate for errors, batched for others)
 * - Graceful degradation on network failures
 * - Context tracking (platform, strategy, checkId, etc.)
 */
import pino from "pino";
// CloudWatch endpoint configuration
const CLOUDWATCH_ENDPOINT = process.env.CLOUDWATCH_ENDPOINT || "https://6f3lbx6rio2ijg72znfcmeil440bzvaq.lambda-url.us-east-1.on.aws/";
const CLOUDWATCH_API_KEY = process.env.CLOUDWATCH_API_KEY || "zJOhPl8hRGyM7Zgs/BleZ6+Hcw7+ex/ehwPTNrmNODQ=";
const CLOUDWATCH_BATCH_SIZE = Number(process.env.CLOUDWATCH_BATCH_SIZE) || 50;
const CLOUDWATCH_FLUSH_INTERVAL = Number(process.env.CLOUDWATCH_FLUSH_INTERVAL) || 10000; // 10 seconds
const CONFIGURED_LOG_LEVEL = process.env.REAL_ID_LOG_LEVEL || "error";
// Production CloudWatch floor: never ship debug/info browser telemetry to
// CloudWatch in prod. Shipping every debug/info log from every customer session
// cost ~118 GB/mo (~$59/mo) in the `real-id-flow` log group (99.7% of it was
// debug/info, ~0 warnings). Local console output (the `real-id-debug`
// localStorage flag, see isConsoleEnabled) is unaffected — this only gates what
// is transmitted. Override with REAL_ID_LOG_LEVEL only makes it MORE restrictive.
const LOG_LEVEL = process.env.NODE_ENV === "production" &&
    !["warn", "error"].includes(CONFIGURED_LOG_LEVEL)
    ? "warn"
    : CONFIGURED_LOG_LEVEL;
class CloudWatchLogger {
    logBuffer = [];
    context = {};
    flushTimer = null;
    config;
    pino;
    constructor() {
        this.config = {
            endpoint: CLOUDWATCH_ENDPOINT,
            apiKey: CLOUDWATCH_API_KEY,
            batchSize: CLOUDWATCH_BATCH_SIZE,
            flushInterval: CLOUDWATCH_FLUSH_INTERVAL,
            enabled: process.env.NODE_ENV === "production" || process.env.ENABLE_CLOUDWATCH === "true",
        };
        // Initialize pino with browser configuration
        this.pino = pino({
            level: LOG_LEVEL,
            browser: {
                asObject: true,
                serialize: true,
            },
        });
        // Start flush interval timer
        this.startFlushTimer();
        // Flush on page unload
        if (typeof window !== "undefined") {
            window.addEventListener("beforeunload", () => {
                this.flush().catch(() => {
                    // Silently fail on unload
                });
            });
        }
    }
    /**
     * Check if logging is enabled for console output
     */
    isConsoleEnabled() {
        if (typeof window === "undefined")
            return false;
        return (process.env.NODE_ENV === "development" ||
            window.localStorage.getItem("real-id-debug") !== null);
    }
    /**
     * Check if log level should be processed
     */
    shouldLog(level) {
        const levels = {
            debug: 0,
            info: 1,
            warn: 2,
            error: 3,
        };
        const currentLevel = levels[LOG_LEVEL];
        const messageLevel = levels[level];
        return messageLevel >= currentLevel;
    }
    /**
     * Set persistent context for all log messages
     */
    setContext(context) {
        this.context = { ...this.context, ...context };
    }
    /**
     * Sanitize args array to remove circular references
     */
    sanitizeArgs(args) {
        if (!args || args.length === 0)
            return undefined;
        return args.map(arg => {
            // Keep primitives as-is
            if (arg === null || arg === undefined)
                return arg;
            if (typeof arg === "string" || typeof arg === "number" || typeof arg === "boolean") {
                return arg;
            }
            // Parse objects through safeStringify and back to avoid circular refs
            try {
                const stringified = this.safeStringify(arg);
                return JSON.parse(stringified);
            }
            catch (e) {
                return "[Unserializable]";
            }
        });
    }
    /**
     * Create a log entry with context
     */
    createLogEntry(level, message, args) {
        return {
            level,
            message,
            timestamp: Date.now(),
            context: {
                ...this.context,
                url: typeof window !== "undefined" ? window.location.href : undefined,
                userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
            },
            args: this.sanitizeArgs(args),
        };
    }
    /**
     * Add log to buffer and potentially send
     */
    async handleLog(level, message, args) {
        if (!this.shouldLog(level))
            return;
        const entry = this.createLogEntry(level, message, args);
        // Always add to buffer
        this.logBuffer.push(entry);
        // Immediate send for errors
        if (level === "error") {
            await this.sendImmediately([entry]);
            // Remove from buffer since it was sent
            const index = this.logBuffer.indexOf(entry);
            if (index > -1) {
                this.logBuffer.splice(index, 1);
            }
        }
        // Flush if buffer is full
        else if (this.logBuffer.length >= this.config.batchSize) {
            await this.flush();
        }
    }
    /**
     * Safe JSON serialization that handles circular references
     */
    safeStringify(obj) {
        const seen = new WeakSet();
        return JSON.stringify(obj, (key, value) => {
            // Handle circular references
            if (typeof value === "object" && value !== null) {
                if (seen.has(value)) {
                    return "[Circular]";
                }
                seen.add(value);
            }
            // Handle Error objects
            if (value instanceof Error) {
                return {
                    name: value.name,
                    message: value.message,
                    stack: value.stack,
                };
            }
            return value;
        });
    }
    /**
     * Send logs to CloudWatch endpoint immediately
     */
    async sendImmediately(entries) {
        if (!this.config.enabled || entries.length === 0)
            return;
        try {
            const response = await fetch(this.config.endpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${this.config.apiKey}`,
                },
                body: this.safeStringify({
                    logs: entries,
                    source: "real-id-flow",
                }),
                // Don't wait for response to avoid blocking
                keepalive: true,
            });
            if (!response.ok) {
                // Silently fail - don't block the application
                console.warn(`CloudWatch logging failed: ${response.status}`);
            }
        }
        catch (error) {
            // Silently fail - don't block the application
            console.warn("CloudWatch logging error:", error);
        }
    }
    /**
     * Flush buffered logs to CloudWatch
     */
    async flush() {
        if (this.logBuffer.length === 0)
            return;
        const logsToSend = [...this.logBuffer];
        this.logBuffer = [];
        await this.sendImmediately(logsToSend);
    }
    /**
     * Start the automatic flush timer
     */
    startFlushTimer() {
        if (typeof window === "undefined")
            return;
        this.flushTimer = setInterval(() => {
            this.flush().catch(() => {
                // Silently fail
            });
        }, this.config.flushInterval);
    }
    /**
     * Safely serialize a value for logging
     */
    serializeValue(value) {
        if (value === null || value === undefined) {
            return String(value);
        }
        if (typeof value === "string") {
            return value;
        }
        if (typeof value === "number" || typeof value === "boolean") {
            return String(value);
        }
        if (value instanceof Error) {
            return `${value.message}\n${value.stack}`;
        }
        // For objects, use safeStringify
        try {
            return this.safeStringify(value);
        }
        catch (e) {
            return "[Unserializable Object]";
        }
    }
    /**
     * Console.log replacement
     */
    log(...args) {
        if (this.isConsoleEnabled()) {
            console.log.apply(console, args);
        }
        const message = args.map(arg => this.serializeValue(arg)).join(" ");
        this.handleLog("info", message, args).catch(() => {
            // Silently fail
        });
    }
    /**
     * Console.debug replacement
     */
    debug(...args) {
        if (this.isConsoleEnabled()) {
            console.debug.apply(console, args);
        }
        const message = args.map(arg => this.serializeValue(arg)).join(" ");
        this.handleLog("debug", message, args).catch(() => {
            // Silently fail
        });
    }
    /**
     * Console.info replacement
     */
    info(...args) {
        if (this.isConsoleEnabled()) {
            console.info.apply(console, args);
        }
        const message = args.map(arg => this.serializeValue(arg)).join(" ");
        this.handleLog("info", message, args).catch(() => {
            // Silently fail
        });
    }
    /**
     * Console.warn replacement
     */
    warn(...args) {
        if (this.isConsoleEnabled()) {
            console.warn.apply(console, args);
        }
        const message = args.map(arg => this.serializeValue(arg)).join(" ");
        this.handleLog("warn", message, args).catch(() => {
            // Silently fail
        });
    }
    /**
     * Console.error replacement
     */
    error(...args) {
        if (this.isConsoleEnabled()) {
            console.error.apply(console, args);
        }
        const message = args.map(arg => this.serializeValue(arg)).join(" ");
        this.handleLog("error", message, args).catch(() => {
            // Silently fail
        });
    }
    /**
     * Console.table replacement
     */
    table(...args) {
        if (this.isConsoleEnabled()) {
            console.table.apply(console, args);
        }
        // For CloudWatch, send as structured data
        const message = "Table data";
        this.handleLog("info", message, args).catch(() => {
            // Silently fail
        });
    }
    /**
     * Console.dir replacement
     */
    dir(...args) {
        if (this.isConsoleEnabled()) {
            console.dir.apply(console, args);
        }
        // For CloudWatch, send as structured data
        const message = "Object data";
        this.handleLog("debug", message, args).catch(() => {
            // Silently fail
        });
    }
}
// Create and export singleton instance
const logger = new CloudWatchLogger();
export default logger;
